import React, { Fragment, useRef, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useHandleOpenViewerClick } from "../../../media-viewer/mediaViewerRedux";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { isBlank, notBlank } from "../../../util/ext";
import { getIds } from "../../../util/mediaUtil";
import { equals, intersection, isEmpty } from "ramda";
import { useMediaTable } from "../../common/use-mediaTable";
import { useConfirm } from "../../../common/use-confirm";

import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";

import ConfirmDialog from "../../../components/ConfirmDialog";
import ModuleFilesRow from "./ModuleFilesRow";
import { useSelected } from "../../organize/OrganizeProvider";

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: "20px",
  },

  titleCell: {
    width: "30%",
  },

  includedInCell: {
    paddingLeft: "15px",
    width: "15%",
  },

  originCell: {
    width: "10%",
    paddingLeft: 20,
  },

  medCell: {
    width: "8%",
  },

  actionsCell: {
    width: "10%",
  },

  smallCell: {
    width: "5%",
  },

  container: {
    padding: 0,
    overflowY: "auto",
    maxHeight: 800,
  },

  empty: {
    textAlign: "center",
    marginTop: theme.spacing(8),
    fontStyle: "italic",
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },

  reviewTitle: {
    paddingLeft: theme.spacing(6),
  },

  table: {
    height: "100%",
    overflowY: "scroll",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const EmptyTableText = props => {
  const classes = useStyles();

  return <p className={classes.empty}>This module has no content.</p>;
};

const ModuleFilesTable = props => {
  const {
    files,
    isLoading,
    module,
    onContentEditClick,
    onItemsDelete,
    onScrollVisible,
    onSortChange,
    onRowsPerPageChange,
    order,
    orderBy,
    setLoading,
    searchState,
    sourceFilter,
  } = props;

  const classes = useStyles();
  const { SnackAlert, showAlert } = useSnackAlert();
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;
  const { selectedContent, appendToSelectedContent } = useSelected();

  const { handleRequestSort } = useMediaTable(
    {
      onSortChange,
      onRowsPerPageChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
    },
  );

  const { handleOpenViewerClick, isViewerLoading } = useHandleOpenViewerClick();

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl) && notBlank(files)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible, files]);

  //-- Manage Checkbox State
  const [checkbox, setCheckbox] = useState({
    checked: false,
    indeterminate: false,
  });
  useEffect(() => {
    const selectedIdsForPage = intersection(
      getIds(files),
      getIds(selectedContent),
    );
    if (!isEmpty(selectedIdsForPage)) {
      setCheckbox({
        checked: equals(selectedIdsForPage.length, files.length),
        indeterminate: !equals(selectedIdsForPage.length, files.length),
      });
    } else {
      setCheckbox({ checked: false, indeterminate: false });
    }
  }, [files, selectedContent]);

  const handleChange = _event => {
    appendToSelectedContent(checkbox, files);
  };

  return (
    <Paper elevation={0} className={classes.root}>
      {isBlank(files) && !isLoading ? (
        <EmptyTableText />
      ) : (
        <Fragment>
          <SnackAlert />
          <ConfirmDialog
            useCustomTitle
            useCustomMessage
            handler={confirmHandler}
          />
          <TableContainer ref={tableContainerEl} className={classes.container}>
            <Table stickyHeader aria-label="Content" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    align="center"
                    className={classes.smallCell}
                  >
                    <Checkbox
                      color="primary"
                      indeterminate={checkbox.indeterminate}
                      checked={checkbox.checked}
                      onChange={handleChange}
                      inputProps={{ "aria-label": "Select" }}
                    />
                  </TableCell>

                  <TableCell
                    key="content"
                    align="left"
                    className={classes.titleCell}
                  >
                    <TableSortLabel
                      active={orderBy === "title"}
                      direction={order}
                      onClick={() => {
                        handleRequestSort("title");
                      }}
                    >
                      Title
                      {orderBy === "title" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    key="date-updated"
                    align="left"
                    className={classes.medCell}
                  >
                    <TableSortLabel
                      active={orderBy === "updated"}
                      direction={order}
                      onClick={() => {
                        handleRequestSort("updated");
                      }}
                    >
                      Updated
                      {orderBy === "updated" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    key="content-type"
                    align="left"
                    className={classes.medCell}
                  >
                    <TableSortLabel
                      active={orderBy === "type"}
                      direction={order}
                      onClick={() => handleRequestSort("type")}
                    >
                      Type
                      {orderBy === "type" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    key="origin"
                    align="left"
                    className={classes.originCell}
                  >
                    Origin
                  </TableCell>

                  <TableCell
                    key="included-in"
                    align="left"
                    className={classes.includedInCell}
                  >
                    Included In
                  </TableCell>

                  <TableCell
                    key="actions"
                    align="center"
                    className={classes.actionsCell}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {files.map((content, index) => (
                  <ModuleFilesRow
                    index={index}
                    loading={isViewerLoading}
                    key={content.id}
                    content={content}
                    module={module}
                    handleOpenViewerClick={handleOpenViewerClick}
                    onItemsDelete={onItemsDelete}
                    onContentEditClick={onContentEditClick}
                    setLoading={setLoading}
                    searchState={searchState}
                    showConfirm={showConfirm}
                    sourceFilter={sourceFilter}
                    showAlert={showAlert}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Fragment>
      )}
    </Paper>
  );
};

export default ModuleFilesTable;
